import React, { useState, useRef, useEffect } from 'react';
import { Image } from '@adobe/react-spectrum';
import { getCategoryIcon } from '../../../utils/categoryIconUtils';
import { AllIconsPopup } from './AllIconsPopup';
import styles from './TimelineCard.module.scss';

interface ProductIconsProps {
  categories?: string[];
  products?: string[];
  onError?: (event: any) => void;
  maxVisible?: number; // Maximum number of icons to show before displaying +X
}

export const ProductIcons: React.FC<ProductIconsProps> = ({
  categories,
  products,
  onError,
  maxVisible = 3 // Default to showing max 3 icons
}) => {
  const [showAllIcons, setShowAllIcons] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);

  // Close tooltip when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        tooltipRef.current &&
        buttonRef.current &&
        !tooltipRef.current.contains(event.target as Node) &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setShowAllIcons(false);
      }
    };

    if (showAllIcons) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showAllIcons]);
  // Combine categories and products into a single array
  const allItems: Array<{ type: 'category' | 'product'; value: string }> = [];

  if (categories && categories.length > 0) {
    categories.forEach(cat => allItems.push({ type: 'category', value: cat }));
  }

  if (products && products.length > 0) {
    products.forEach(prod => allItems.push({ type: 'product', value: prod }));
  }

  // Limit visible items
  const visibleItems = allItems.slice(0, maxVisible);
  const remainingItems = allItems.slice(maxVisible); // Get only the items after maxVisible
  const remainingCount = Math.max(0, allItems.length - maxVisible);

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        flexWrap: 'nowrap'
      }}
    >
      {visibleItems.map((item, index) => (
        <Image
          key={`${item.type}-${item.value}-${index}`}
          src={
            item.type === 'category'
              ? getCategoryIcon([item.value], [])
              : getCategoryIcon([item.value], [item.value])
          }
          alt={item.value}
          UNSAFE_className={styles.tooltipIcon}
          onError={onError}
        />
      ))}

      {remainingCount > 0 && (
        <div style={{ position: 'relative' }}>
          <div
            ref={buttonRef}
            onClick={() => setShowAllIcons(!showAllIcons)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '36px',
              height: '36px',
              borderRadius: '4px',
              backgroundColor: showAllIcons ? '#e0e0e0' : '#f5f5f5',
              fontSize: '12px',
              fontWeight: 600,
              color: '#464646',
              flexShrink: 0,
              cursor: 'pointer',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={e => {
              if (!showAllIcons) {
                e.currentTarget.style.backgroundColor = '#e0e0e0';
              }
            }}
            onMouseLeave={e => {
              if (!showAllIcons) {
                e.currentTarget.style.backgroundColor = '#f5f5f5';
              }
            }}
          >
            +{remainingCount}
          </div>
          {showAllIcons && (
            <AllIconsPopup
              allItems={remainingItems}
              onError={onError}
              tooltipRef={tooltipRef}
            />
          )}
        </div>
      )}
    </div>
  );
};
